import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { trackEvent } from '../utils/analytics'
import api, { extractErrorMessage } from '../api/client'
import { useAuthStore } from '../stores/authStore'
import {
  useCreateLearningSession,
  useNextLearningCard,
  usePauseLearningSession,
  useResumeSession,
  useRetype,
  useSubmitAnswer,
} from './useLearning'
import { useProgressSummary, useStartSmartQueue } from './useProgress'
import { addTodayCorrect, injectShakeKeyframes } from '../utils/learningUtils'
import { ResolutionReporter } from '../utils/resolutionReports'
import { findTermSide, isTermSide } from '../utils/cardSides'
import { learnDefinitionSide, learnExampleSide } from '../utils/learnCardFields'
import { checkRetypeAnswer } from '../utils/answerNormalize'
import { detectMilestone, type Milestone } from '../utils/milestones'
import type {
  GrammarNote, LearningAnswerResult, LearningNextCard,
  LearningSessionStatus, LessonPlan, MistakeRecord, NextCardData, PrepStats, ProgressSummary,
} from '../types'

// Wire shape of GET /sessions/{id}/mistakes (backend/app/schemas/learning.py MistakeItem) —
// snake_case, matching the API convention everywhere else in this file.
interface MistakeItemDTO {
  card_id: string
  term: string
  form_label: string | null
  user_answer: string | null
  correct_answer: string
  definition: string | null
  translation: string | null
}

function mapMistakeDTO(m: MistakeItemDTO): MistakeRecord {
  return {
    cardId: m.card_id,
    term: m.term,
    formLabel: m.form_label,
    userAnswer: m.user_answer ?? '',
    correctAnswer: m.correct_answer,
    definition: m.definition,
    translation: m.translation,
  }
}

// ── Types shared with LearnPage ────────────────────────────────────────────────

export type Phase = 'loading' | 'error' | 'empty' | 'active' | 'complete'
export type ResultState = 'none' | 'correct' | 'wrong' | 'retype' | 'soft_reject'

interface Params {
  sourceType: 'deck' | 'folder' | 'course'
  sourceId: string | undefined
  sqState: { sessionId?: string; totalCards?: number; resolutionCards?: number; reviewedCards?: number; reviewedCorrect?: number; reviewedWrong?: number; from?: string; is_prep?: boolean; sessionTitle?: string; dayBoundaryClosed?: boolean } | null
  speak: (word: string, onEnd?: () => void) => void
  /** Stops in-flight speech without triggering the onEnd callback. */
  cancelSpeech: () => void
  isSpeechSupported: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Dynamic TTS cap: allows ~600ms per word + 1s lead-in, clamped to [4s, 15s].
 * Prevents a stuck synthesis engine from freezing the session indefinitely while
 * still giving long example sentences (~20 words ≈ 13s) enough time to finish.
 */
function _ttsCap(text: string): number {
  const wordCount = text.trim().split(/\s+/).length
  return Math.min(Math.max(4000, wordCount * 600 + 1000), 15000)
}

/** True iff *err* carries the backend's SESSION_DAY_ENDED code (ADR-082) — a
 *  session that began before the day boundary was completed and the request
 *  refused. The only trigger for the boundary overlay; nothing here computes
 *  a day on the client. */
function isDayEndedError(err: unknown): boolean {
  const code = (err as { response?: { data?: { detail?: { code?: string } } } })
    ?.response?.data?.detail?.code
  return code === 'SESSION_DAY_ENDED'
}

/** HTTP status of an axios-style error, if it carries one. */
function httpStatusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useLearningSession({ sourceType, sourceId, sqState, speak, cancelSpeech, isSpeechSupported }: Params) {
  const navigate = useNavigate()
  const qc = useQueryClient()

  // ── Session state ───────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState<string | undefined>()
  const [phase, setPhase] = useState<Phase>('loading')
  // True when an existing paused session was auto-resumed on mount.
  const [isResumed, setIsResumed] = useState(false)
  const [lessonPlan, setLessonPlan] = useState<LessonPlan | null>(null)
  const [prepStats, setPrepStats] = useState<PrepStats | null>(null)
  // ADR-112 Decision 6: a practice session (Train, Quick prep, topic training), in
  // which no answer changes a word's level. Learned from the server — the create
  // response on the source path, the served card on every path — and sticky for
  // the session, so the completion screen still knows after the last card.
  const [isPractice, setIsPractice] = useState(false)
  const [blockStats, setBlockStats] = useState<Record<string, { correct: number; wrong: number }>>({})

  // ── Card state ──────────────────────────────────────────────────────────────
  const [currentCard, setCurrentCard] = useState<LearningNextCard | null>(null)
  const [prevCard, setPrevCard] = useState<LearningNextCard | null>(null)
  const [showingPrev, setShowingPrev] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [lastResult, setLastResult] = useState<LearningAnswerResult | null>(null)
  const [resultState, setResultState] = useState<ResultState>('none')
  const [activeTab, setActiveTab] = useState(0)
  const [showAll, setShowAll] = useState(false)
  const [showDefinitionInline, setShowDefinitionInline] = useState(false)
  const [isShaking, setIsShaking] = useState(false)
  const [hintLetters, setHintLetters] = useState(0)
  const [usedHint, setUsedHint] = useState(false)
  const [pendingGrammarNote, setPendingGrammarNote] = useState<GrammarNote | null>(null)
  const [mcSelectedOption, setMcSelectedOption] = useState<string | null>(null)

  // ── Stats ───────────────────────────────────────────────────────────────────
  const [totalCards, setTotalCards] = useState(0)
  // resolutionCount tracks conveyor resolutions (Lingvist-style): increments on every
  // correct answer of any exercise type, every accepted retype, and every wrong-MC
  // advance. M = total_cards is the budget of presented slots; N = resolutions used.
  // The graduated field from the server is kept for mastery/FSRS but does not drive
  // this counter. Server-side reconstruction requires a migration (see task report).
  const [resolutionCount, setResolutionCount] = useState(0)
  // Ref mirrors resolutionCount so doAdvance can read the current value synchronously
  // (React state updates are batched and the closure would see the pre-increment value).
  const resolutionCountRef = useRef(0)
  const [stats, setStats] = useState({ total: 0, correct: 0, wrong: 0 })
  // Initialized to 0; set from the server completion snapshot (not localStorage) to
  // prevent cumulative event counts from leaking into the completion screen.
  const [wordsLearnedToday, setWordsLearnedToday] = useState(0)
  // Client-only, in-memory record of this session's mistakes for the read-only
  // review list on the completion screen. Never persisted, never sent to the
  // server. One entry per card (first wrong attempt wins) — see recordMistake.
  const [mistakes, setMistakes] = useState<MistakeRecord[]>([])
  const [errorMsg, setErrorMsg] = useState('')
  const [nextDueAt, setNextDueAt] = useState<string | null>(null)
  const [continueExhausted, setContinueExhausted] = useState(false)
  // ADR-082 boundary overlay. Set only from the server's own signal — never
  // computed from a client clock — and only in the two named situations:
  // 'interrupted' — a session mid-study was closed by the boundary; the request
  //   that discovered it (next-card / answer / retype) is what set this, and
  //   currentCard is deliberately left untouched so it stays visible, frozen,
  //   behind the overlay.
  // 'returned' — the session created on arrival replaces one the boundary had
  //   already closed (day_boundary_closed_previous on the create response).
  // null in every other case, including "nothing to resume" and an ordinary
  // session end — the overlay must never render then.
  const [dayBoundaryClosed, setDayBoundaryClosed] = useState<'interrupted' | 'returned' | null>(null)
  // True while the continue button is rebuilding a session after an
  // interruption — disables the button so a second tap cannot fire a second create.
  const [restartingSession, setRestartingSession] = useState(false)

  // ── Refs ────────────────────────────────────────────────────────────────────
  const awaitingCard = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // True once session ends naturally — prevents redundant pause on unmount.
  const sessionEndedRef = useRef(false)
  // Tracks current sessionId for event handlers registered outside React's render cycle.
  const sessionIdRef = useRef<string | undefined>(undefined)
  // Set by pagehide so pageshow knows to re-activate after a best-effort pause.
  const pausedByVisibilityRef = useRef(false)
  // Two separate guards so a correct answer always fires even after the wrong verdict spoke.
  const speechFiredWrongRef = useRef(false)
  const speechFiredCorrectRef = useRef(false)
  // Whether the last wrong result can be retried (card re-queued) vs must advance (session ending).
  const wrongCanRetryRef = useRef(false)
  // 'text' = standard typed input; 'other' = MC / sentence-build / listening (keep old flow).
  const wrongExerciseModeRef = useRef<'text' | 'other'>('text')
  // Set to true when retype succeeded so the correct-state effect skips re-speaking the example.
  const retypeSucceededRef = useRef(false)
  const advanceFallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Stores the pending advance function so manual advance (Enter key) can fire it directly
  // without relying on the TTS onEnd callback path.
  const pendingAdvanceRef = useRef<(() => void) | null>(null)
  // Tracks which card IDs have been submitted at least once (deduplicate session stats).
  const firstAttemptedRef = useRef(new Set<string>())
  // ADR-086 Decision 1: a resolution the client completes on its own is reported
  // with the event that created it. The deferred one-shot flag this replaced is
  // gone outright, not disabled — there is no client path that holds a completed
  // resolution for a later call.
  const reporterRef = useRef<ResolutionReporter | null>(null)
  if (reporterRef.current === null) {
    reporterRef.current = new ResolutionReporter()
  }
  // Guards one accepted retype to exactly one report identity. The server's
  // unique constraint is the at-most-once guarantee (ADR-086 Decision 4); this
  // stops a double-invoked handler from minting a *second* identity for one
  // resolution, which no constraint could catch. StrictMode double-invokes
  // effects and handlers in development, and the diagnostic (§3) records that
  // this client already produces paired effects in the wild.
  const reportedRetypesRef = useRef(new Set<string>())
  // Prevents showing a grammar note more than once per session.
  const grammarNoteShownRef = useRef(false)
  // ADR-103 Decision 7 — recovery from a session the server no longer accepts.
  // Session ids already recovered from: a refused id is acted on once, however many
  // requests (status, resume, next-card) go on to discover it.
  const recoveredFromRef = useRef(new Set<string>())
  // Recoveries since a card was last served. A second one in a row means the fresh
  // session was unusable too — show the error screen rather than loop.
  const consecutiveRecoveriesRef = useRef(0)
  // Source type of an sqState session, read from GET /sessions/{id}. Decides which
  // start a recovery goes through; null until that read returns.
  const sqSourceTypeRef = useRef<string | null>(null)
  // Captures grammar note for async advance callback (avoids stale closure on currentCard).
  const currentCardGrammarNoteRef = useRef<GrammarNote | null>(null)

  // ── API hooks ───────────────────────────────────────────────────────────────
  const createSession = useCreateLearningSession()
  const startSmartQueue = useStartSmartQueue()
  const submitAnswer = useSubmitAnswer(sessionId)
  const retypeMutation = useRetype(sessionId)
  const pauseSession = usePauseLearningSession()
  const resumeSession = useResumeSession()
  const nextCardQuery = useNextLearningCard(sessionId)
  const { data: progressSummary } = useProgressSummary()

  // Snapshot the cumulative words-known total at the START of the CURRENT session — the
  // baseline vocabulary milestones cross against. Captured once for the very first session
  // of this page mount (from the live query, best-effort) and then re-seeded explicitly by
  // resetForNewSession for every chained "Continue learning" session from
  // `lastSessionWordsKnownRef` (the previous session's own resolved post-session total) —
  // never left pointing at a pre-session value from two sessions ago.
  const preWordsKnownRef = useRef<number | null>(null)
  useEffect(() => {
    if (preWordsKnownRef.current === null && progressSummary) {
      preWordsKnownRef.current = progressSummary.words_known_count
    }
  }, [progressSummary])

  // Resolved post-session words-known total for the session whose completion screen is
  // currently (or was most recently) shown. Set exactly once per session, deterministically
  // awaited as part of the transition into 'complete' (see resolveWordsKnownAndComplete)
  // rather than left to a background refetch race with the live `progressSummary` query.
  // Also becomes the next chained session's baseline via resetForNewSession.
  const [postSessionWordsKnown, setPostSessionWordsKnown] = useState<number | null>(null)
  const lastSessionWordsKnownRef = useRef<number | null>(null)

  // Milestone (streak or vocabulary) hit by this session, if any — a moment, not a
  // status: recomputed from resolved data, not persisted. Vocabulary detection uses
  // `postSessionWordsKnown` (deterministically resolved before phase flips to 'complete'),
  // never the live `progressSummary` query directly, so it can't race a navigation.
  const milestone = useMemo<Milestone | null>(() => {
    if (phase !== 'complete') return null
    return detectMilestone(
      preWordsKnownRef.current,
      postSessionWordsKnown,
      progressSummary?.streak_days,
    )
  }, [phase, postSessionWordsKnown, progressSummary?.streak_days])

  // Deterministically resolve the post-session vocabulary total before flipping to the
  // 'complete' phase. `progress-summary` must already have been invalidated by the caller
  // so this refetches rather than returning the pre-session cached value. Awaited so the
  // completion screen's first render — not a later one that may never come if the user
  // navigates on — always has the correct total for both milestone detection and the
  // support line (same number, by construction: both read `postSessionWordsKnown`).
  const resolveWordsKnownAndComplete = useCallback(async () => {
    try {
      const data = await qc.fetchQuery<ProgressSummary>({
        queryKey: ['progress-summary'],
        queryFn: () => api.get('/api/v1/progress/summary/').then((r) => r.data),
      })
      lastSessionWordsKnownRef.current = data.words_known_count
      setPostSessionWordsKnown(data.words_known_count)
    } catch {
      // Network error: fall back to whatever the live query already holds (may be the
      // pre-session value — no milestone fires rather than a wrong one).
      lastSessionWordsKnownRef.current = progressSummary?.words_known_count ?? null
      setPostSessionWordsKnown(progressSummary?.words_known_count ?? null)
    }
    setPhase('complete')
  }, [qc, progressSummary])

  // ── Keep sessionIdRef in sync so event handlers always read the latest value ─
  useEffect(() => { sessionIdRef.current = sessionId }, [sessionId])

  // ── Persist session pointer for reload/cold-start recovery (audit R2) ────────
  useEffect(() => {
    if (sessionId) sessionStorage.setItem('cs_active_session', sessionId)
  }, [sessionId])

  // Clear the persisted pointer when the session ends naturally — or on the error
  // screen, so neither Retry nor a reload can land back on a refused session id
  // (ADR-103 Decision 7).
  useEffect(() => {
    if (phase === 'complete' || phase === 'empty' || phase === 'error') {
      sessionStorage.removeItem('cs_active_session')
    }
  }, [phase])

  // ── Best-effort pause on pagehide (tab close / navigation / Android task-switch) ─
  // pageshow re-activates so returning to the page mid-session stays seamless.
  // pauseSession.mutate / resumeSession.mutate are stable TanStack Query v5 refs;
  // current sessionId is read via sessionIdRef so the listener never needs re-registration.
  useEffect(() => {
    const handlePageHide = () => {
      const sid = sessionIdRef.current
      if (sid && !sessionEndedRef.current) {
        pausedByVisibilityRef.current = true
        // keepalive fetch is guaranteed by the browser to flush even during page teardown
        // (hard reload, tab close). The plain XHR inside pauseSession.mutate is aborted
        // by the browser before it reaches the network in those cases.
        const token = useAuthStore.getState().accessToken
        fetch(`/api/v1/learning/sessions/${sid}/pause`, {
          method: 'PATCH',
          keepalive: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }).catch(() => {})
        // Also fire the mutation for BFCache tab-switch: its onSuccess query invalidation
        // keeps Home's session queries fresh after a normal return from the page.
        pauseSession.mutate(sid)
      }
    }
    const handlePageShow = () => {
      const sid = sessionIdRef.current
      if (sid && !sessionEndedRef.current && pausedByVisibilityRef.current) {
        pausedByVisibilityRef.current = false
        resumeSession.mutate(sid)
      }
    }
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('pageshow', handlePageShow)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Derived speech values ───────────────────────────────────────────────────
  const termToSpeak = (currentCard ? findTermSide(currentCard.other_sides) : undefined)?.content ?? ''

  // ADR-104: the example VALUE (first non-blank Example), so a blank field in
  // front does not reduce what is spoken to the term alone.
  // Same lookup as LearnPage's sentence, so what is spoken is what was shown.
  const rawExampleContent =
    (currentCard ? learnExampleSide(currentCard)?.content : undefined) ??
    (currentCard?.prompt.kind === 'example' ? currentCard.prompt.content : undefined)

  // Text spoken after a correct answer: example with placeholder replaced, or term alone.
  const promptToSpeak = (() => {
    if (!rawExampleContent) return termToSpeak
    if (!termToSpeak) return rawExampleContent
    return rawExampleContent.replace(/_+/g, termToSpeak)
  })()

  // ── Multiple-choice options (shuffled once per card) ────────────────────────
  const mcOptions = useMemo<string[]>(() => {
    if (!currentCard || currentCard.exercise_format !== 'multiple_choice') return []
    const term = findTermSide(currentCard.other_sides)?.content
      ?? (isTermSide(currentCard.prompt) ? currentCard.prompt.content : '')
    const distractors = currentCard.distractors ?? []
    if (!term || distractors.length < 3) return []
    const opts = [term, ...distractors.slice(0, 3)]
    for (let i = opts.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[opts[i], opts[j]] = [opts[j], opts[i]]
    }
    return opts
  }, [currentCard])

  // Offer any outstanding resolution report again alongside a session-scoped
  // call (ADR-086 Decision 3b). Deliberately NOT awaited by its callers: the
  // learner's answer must not wait on a report for a card that already left the
  // screen, and Decision 2 forbids adding latency to the answer path.
  const reofferReports = useCallback(() => {
    void reporterRef.current?.flush()
  }, [])

  // Capture a wrong answer for the completion-screen review list. Must be called
  // with the raw input the user actually typed/selected, before the mandatory
  // retype step overwrites it. One entry per card: a card wrong more than once
  // in the intra-session recall loop keeps only its first mistake, since that's
  // the attempt that counts toward stats.wrong.
  const recordMistake = useCallback((card: LearningNextCard, correctAnswer: string, userAnswer: string) => {
    setMistakes((prev) => {
      if (prev.some((m) => m.cardId === card.card_id)) return prev
      const termSide = findTermSide(card.other_sides)
      // ADR-104 Decision 5: definition and form from the definition VALUE
      // side, the one the learner was prompted with — prompt first, since a
      // Definition prompt is already the card's value.
      const definitionSide = learnDefinitionSide(card)
      const term = termSide?.content ?? (isTermSide(card.prompt) ? card.prompt.content : correctAnswer)
      const formLabel = definitionSide?.form ?? card.prompt.form ?? null
      const definition = definitionSide?.content ?? null
      return [...prev, {
        cardId: card.card_id,
        term,
        formLabel,
        userAnswer,
        correctAnswer,
        definition,
        translation: card.translation ?? null,
      }]
    })
  }, [])

  // ── Reset all state for a fresh in-place session ────────────────────────────
  const resetForNewSession = useCallback(() => {
    sessionEndedRef.current = false
    awaitingCard.current = false
    firstAttemptedRef.current = new Set()
    // A fresh session gets a fresh retype-identity guard. Outstanding reports are
    // deliberately NOT cleared here: they belong to the session that produced
    // them and are settled against it (ADR-086 Decision 3b/5), not abandoned
    // because a new session started.
    reportedRetypesRef.current = new Set()
    speechFiredWrongRef.current = false
    speechFiredCorrectRef.current = false
    retypeSucceededRef.current = false
    wrongExerciseModeRef.current = 'text'
    setSessionId(undefined)
    setPhase('loading')
    resolutionCountRef.current = 0
    setResolutionCount(0)
    setStats({ total: 0, correct: 0, wrong: 0 })
    setMistakes([])
    setResultState('none')
    setCurrentCard(null)
    setUserInput('')
    setLastResult(null)
    setHintLetters(0)
    setUsedHint(false)
    setPrevCard(null)
    setShowingPrev(false)
    setIsResumed(false)
    setErrorMsg('')
    setLessonPlan(null)
    setPrepStats(null)
    setIsPractice(false)
    setBlockStats({})
    setContinueExhausted(false)
    // Re-seed the milestone baseline to the JUST-FINISHED session's own resolved
    // post-session total — not left pointing at this page-mount's original value.
    // Without this, a threshold crossed in session N would look "crossed again" in
    // every subsequent chained session, since preWordsKnownRef would still predate N.
    preWordsKnownRef.current = lastSessionWordsKnownRef.current
    setPostSessionWordsKnown(null)
  }, [])

  // ── Recovery failure (ADR-103 Decision 7) ───────────────────────────────────
  // The error screen is reached only when recovery itself fails. Its text is the
  // localized default (LearnPage renders tr.learn.sessionError for an empty
  // errorMsg); the refused id is dropped from sessionStorage so Retry and a reload
  // cannot land on it again.
  const failRecovery = useCallback(() => {
    sessionEndedRef.current = true
    awaitingCard.current = false
    sessionStorage.removeItem('cs_active_session')
    setRestartingSession(false)
    setErrorMsg('')
    setPhase('error')
  }, [])

  // ── Start (or restart) a source-bound session ───────────────────────────────
  // Deck/folder/course path (Bypass A). Used both on mount and by the boundary
  // overlay's continue button when it needs to rebuild a session that was
  // closed mid-study (ADR-082 'interrupted') — one code path for both, so the
  // fresh session on the button's success is built exactly the same way as an
  // ordinary session start, not a special case of it.
  // Uses mutateAsync (a plain Promise) rather than mutate's per-call onSuccess/
  // onError callbacks. Verified on device: under React 18 StrictMode's dev-only
  // double-invoke of this effect, two POST /sessions genuinely fire, but only
  // ONE of the two mutate()-style callbacks is ever delivered — which one is
  // not deterministic, so the boundary flag a callback sets can be silently
  // lost if the surviving callback happens to belong to the other call. A
  // Promise from mutateAsync settles by ordinary JS semantics regardless of
  // that — this sidesteps the failure mode rather than working around it.
  const beginSourceSession = useCallback(async (recovery?: { refusedId: string; dayEnded: boolean }) => {
    if (!sourceId) return
    try {
      const data = await createSession.mutateAsync(
        { source_type: sourceType, source_id: sourceId, is_prep: sqState?.is_prep ?? false }
      )
      setRestartingSession(false)
      if (recovery && data.session_id === recovery.refusedId) {
        // The fresh start handed back the session just refused — recovery failed.
        failRecovery()
        return
      }
      if (!data.is_resumed && data.total_cards === 0) {
        sessionEndedRef.current = true
        setPhase('empty')
        return
      }
      setTotalCards(data.total_cards)
      if (data.is_resumed) {
        setIsResumed(true)
        // Seed the progress counter from the authoritative resolution_count in the
        // create response (ADR-046) — NOT (total − remaining), which is the
        // graduation count and undercounts in-flight non-graduated resolutions.
        // This is Bypass A: source-bound routes (/decks/:id/learn etc.) that
        // skip the sqState branch entirely and land here instead.
        resolutionCountRef.current = data.resolution_count
        setResolutionCount(data.resolution_count)
        // Fetch correct/wrong for the completion-screen stats. Fire-and-forget:
        // stats accumulate correctly going forward even if this call fails.
        api
          .get<LearningSessionStatus>(`/api/v1/learning/sessions/${data.session_id}`)
          .then((r) => {
            const s = r.data
            setStats({ total: s.reviewed, correct: s.correct, wrong: s.wrong })
          })
          .catch(() => { /* non-critical */ })
      }
      if (data.lesson_plan) setLessonPlan(data.lesson_plan)
      if (data.prep_stats) setPrepStats(data.prep_stats)
      if (data.is_practice) setIsPractice(true)
      // ADR-082 — the sole trigger for the "returned" overlay: the server
      // reports this session replaces one the day boundary already closed.
      // Nothing here infers or computes a day; the flag is read, not derived.
      if (data.day_boundary_closed_previous || recovery?.dayEnded) setDayBoundaryClosed('returned')
      awaitingCard.current = true
      trackEvent(
        data.is_resumed ? 'learning_session_resumed' : 'learning_session_started',
        { source_type: sourceType, is_prep: sqState?.is_prep ? 'true' : 'false' },
      )
      void qc.invalidateQueries({ queryKey: ['active-session'] })
      void qc.invalidateQueries({ queryKey: ['active-session-sq'] })
      setSessionId(data.session_id)
    } catch (err: unknown) {
      setRestartingSession(false)
      if (recovery) {
        failRecovery()
        return
      }
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail
      const code = detail && typeof detail === 'object' ? (detail as Record<string, unknown>).code : undefined
      if (code === 'NO_DUE_CARDS') {
        const raw = detail && typeof detail === 'object' ? (detail as Record<string, unknown>).next_due_at : undefined
        setNextDueAt(typeof raw === 'string' ? raw : null)
        setPhase('empty')
        return
      }
      const httpStatus = (err as { response?: { status?: number } })?.response?.status
      if (httpStatus === 403) {
        setErrorMsg('This deck is private. You can only study decks you own or decks that are public.')
      } else if (httpStatus === 404) {
        setErrorMsg('Deck not found.')
      } else {
        setErrorMsg(extractErrorMessage(err, 'Failed to start learning session.'))
      }
      setPhase('error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, sourceId, sqState?.is_prep])

  // ── Recovery from an unusable session (ADR-103 Decision 7) ──────────────────
  // Clears the previous session's state for an in-place replacement, keeping the
  // milestone baseline where it is: resetForNewSession re-seeds it for a *chained*
  // session after a completion, which a replacement is not.
  const resetForReplacement = useCallback(() => {
    const baseline = preWordsKnownRef.current
    resetForNewSession()
    preWordsKnownRef.current = baseline
  }, [resetForNewSession])

  // The stream's start, served in place — the same request Home's main button and
  // /learn make (ADR-103 Decision 1): it returns today's live stream session, or
  // builds one. Used by recovery, by Retry and by the boundary overlay's continue.
  const beginStreamSession = useCallback(async (recovery?: { refusedId: string; dayEnded: boolean }) => {
    try {
      const data = await startSmartQueue.mutateAsync()
      setRestartingSession(false)
      if (recovery && data.session_id === recovery.refusedId) {
        failRecovery()
        return
      }
      if (!data.session_id || data.total_cards === 0) {
        sessionEndedRef.current = true
        setPhase('empty')
        return
      }
      setTotalCards(data.total_cards)
      if (data.is_resumed) {
        setIsResumed(true)
        resolutionCountRef.current = data.resolution_count
        setResolutionCount(data.resolution_count)
        const resumedId = data.session_id
        api
          .get<LearningSessionStatus>(`/api/v1/learning/sessions/${resumedId}`)
          .then((r) => setStats({ total: r.data.reviewed, correct: r.data.correct, wrong: r.data.wrong }))
          .catch(() => { /* non-critical */ })
      }
      if (data.day_boundary_closed_previous || recovery?.dayEnded) setDayBoundaryClosed('returned')
      sqSourceTypeRef.current = 'smart_queue'
      awaitingCard.current = true
      void qc.invalidateQueries({ queryKey: ['active-session'] })
      void qc.invalidateQueries({ queryKey: ['active-session-sq'] })
      // Point the router state at the replacement, so a reload resumes it rather
      // than the id this page was opened with.
      navigate('/session', {
        replace: true,
        state: { ...(sqState ?? {}), sessionId: data.session_id, totalCards: data.total_cards, dayBoundaryClosed: false },
      })
      setSessionId(data.session_id)
    } catch {
      failRecovery()
    }
    // startSmartQueue.mutateAsync is a stable TanStack Query v5 ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failRecovery, navigate, qc, sqState])

  const isStreamSqSession = useCallback(
    () => (sqSourceTypeRef.current !== null
      ? sqSourceTypeRef.current === 'smart_queue'
      // Not yet known: a session opened with a title is a topic-training session.
      : !sqState?.sessionTitle),
    [sqState?.sessionTitle],
  )

  // A 409 on resume or next-card (or a session read back as completed) means the
  // session is no longer usable. Start a fresh one for the same entry point, once,
  // without an error; the error screen is for when that fails.
  const recoverUnusableSession = useCallback((refusedId: string, dayEnded = false) => {
    if (recoveredFromRef.current.has(refusedId)) return
    recoveredFromRef.current.add(refusedId)
    // Never pause (or resume) the refused session again.
    sessionEndedRef.current = true
    awaitingCard.current = false
    sessionStorage.removeItem('cs_active_session')
    consecutiveRecoveriesRef.current += 1
    if (consecutiveRecoveriesRef.current > 1) {
      failRecovery()
      return
    }
    resetForReplacement()
    if (sourceId) {
      void beginSourceSession({ refusedId, dayEnded })
      return
    }
    if (isStreamSqSession()) {
      void beginStreamSession({ refusedId, dayEnded })
      return
    }
    // A one-shot session (topic training, review mistakes) has no start to repeat.
    failRecovery()
  }, [sourceId, beginSourceSession, beginStreamSession, failRecovery, isStreamSqSession, resetForReplacement])

  // The error screen's Retry: repeat the recovery for the same entry point. Never a
  // page reload — that would read a refused id back from the router state.
  const retrySession = useCallback(() => {
    recoveredFromRef.current = new Set()
    consecutiveRecoveriesRef.current = 0
    resetForReplacement()
    if (sourceId) {
      void beginSourceSession()
      return
    }
    if (isStreamSqSession()) {
      void beginStreamSession()
      return
    }
    navigate('/home')
  }, [sourceId, beginSourceSession, beginStreamSession, isStreamSqSession, navigate, resetForReplacement])

  // ── Continue past the boundary overlay ──────────────────────────────────────
  const continueAfterDayBoundary = useCallback(() => {
    if (dayBoundaryClosed === 'returned') {
      // The session that greeted the person on arrival is already the fresh
      // replacement (day_boundary_closed_previous set it, above) — nothing to
      // build, only to dismiss.
      setDayBoundaryClosed(null)
      return
    }
    if (dayBoundaryClosed !== 'interrupted') return
    if (sourceId) {
      // beginSourceSession's onSuccess only ever SETS this flag (see its own
      // comment) — it never clears 'interrupted' back to null, so that has
      // to happen here, on the action that is actually resolving it.
      setDayBoundaryClosed(null)
      setRestartingSession(true)
      beginSourceSession()
      return
    }
    if (isStreamSqSession()) {
      // Stream session, interrupted mid-study: the stream's start builds today's
      // session in place (ADR-103 Decision 1 — the same start Home uses).
      setDayBoundaryClosed(null)
      setRestartingSession(true)
      if (sessionId) recoveredFromRef.current.add(sessionId)
      resetForReplacement()
      void beginStreamSession()
      return
    }
    // A one-shot session (topic training) has no start to repeat here.
    navigate('/home')
  }, [dayBoundaryClosed, sourceId, sessionId, beginSourceSession, beginStreamSession, isStreamSqSession, navigate, resetForReplacement])

  // The overlay's backdrop blocks pointer events into the frozen card and
  // input, but a focused input keeps DOM focus across the interruption — a
  // keyboard Enter would otherwise still reach its onKeyDown handler even
  // though nothing behind the overlay reads as interactive. Blurring on the
  // transition makes that true rather than merely visual.
  useEffect(() => {
    if (dayBoundaryClosed) inputRef.current?.blur()
  }, [dayBoundaryClosed])

  // ── Create session on mount ─────────────────────────────────────────────────
  useEffect(() => {
    injectShakeKeyframes()

    // Smart-queue / Continue path: session already exists.
    // Reactivate it first so POST .../answer passes the status=="active" guard.
    if (sqState?.sessionId) {
      awaitingCard.current = true
      trackEvent('learning_session_started', { source_type: 'smart-queue' })
      // ADR-082 Decision 6: the stream start that opened this page closed
      // yesterday's session to do it — the server's flag, carried in router state.
      if (sqState.dayBoundaryClosed) setDayBoundaryClosed('returned')

      // Common resume path once we have authoritative counter values.
      // Hydrating before setSessionId ensures the header shows N/M from the first
      // render of the first card (no 0→N flicker regardless of entry path).
      // `resolution` seeds the live progress counter (ADR-046); `reviewed` seeds the
      // completion stats — they are DIFFERENT numbers (resolution ≥ reviewed) and must
      // not be conflated: reviewed/correct/wrong stay graduation-based for stats.
      const doResume = (resolution: number, reviewed: number, total: number, correct: number, wrong: number) => {
        setTotalCards(total)
        resolutionCountRef.current = resolution
        setResolutionCount(resolution)
        // Also seed stats so the early-complete path and the completion screen both
        // reflect the work done before the pause.
        setStats({ total: reviewed, correct, wrong })
        const proceedWithSession = () => {
          void qc.invalidateQueries({ queryKey: ['active-session'] })
          void qc.invalidateQueries({ queryKey: ['active-session-sq'] })
          setSessionId(sqState.sessionId!)
        }
        resumeSession.mutate(sqState.sessionId!, {
          onSuccess: proceedWithSession,
          onError: (err) => {
            // ADR-103 Decision 7: a 409 means the session is no longer usable —
            // completed underneath this page, or closed by the day boundary
            // (SESSION_DAY_ENDED, whose one-time message still shows: ADR-082
            // Decision 6). Resume is idempotent for a live session, so a 409 is
            // never "already active". Replace it with a fresh session, no error.
            if (httpStatusOf(err) === 409) {
              recoverUnusableSession(sqState.sessionId!, isDayEndedError(err))
              return
            }
            // Anything else (a network failure) is not a verdict on the session;
            // carry on — the next-card request settles it.
            proceedWithSession()
          },
        })
      }

      // Round-4 amendment: always fetch live so that a hard reload (which preserves
      // history.state and therefore sqState.reviewedCards from session entry) does not
      // seed the counter from the stale entry-time snapshot.
      //
      // Instant placeholder: set counter synchronously from router-state so the header
      // never shows 0/0 for the one-frame gap before the live fetch resolves.
      // This value may be stale on a hard reload; the authoritative fetch below corrects it.
      if (sqState.resolutionCards !== undefined || sqState.reviewedCards !== undefined) {
        setTotalCards(sqState.totalCards ?? 0)
        // Prefer the resolution placeholder; fall back to reviewedCards for router
        // state written before ADR-046. Either way the authoritative fetch corrects it.
        const seed = sqState.resolutionCards ?? sqState.reviewedCards ?? 0
        resolutionCountRef.current = seed
        setResolutionCount(seed)
      }
      // Authoritative seed — always fetch live. `resolution_count` (ADR-046) is the
      // exact conveyor-resolution counter maintained server-side across every path
      // (re-serve corrects, wrong-MC advances, accepted retypes); it matches the live
      // client counter on resume/reload for sessions with wrong answers and retypes.
      // `reviewed` is still used, separately, to seed graduation-based completion stats.
      api
        .get<LearningSessionStatus>(`/api/v1/learning/sessions/${sqState.sessionId}`)
        .then((r) => {
          const s = r.data
          sqSourceTypeRef.current = s.source_type
          if (s.status === 'completed') {
            // Completed underneath this page (another tab, a finished budget): no
            // resume to attempt — recover straight away (ADR-103 Decision 7).
            recoverUnusableSession(sqState.sessionId!)
            return
          }
          doResume(s.resolution_count, s.reviewed, s.total, s.correct, s.wrong)
        })
        .catch((err: unknown) => {
          if (httpStatusOf(err) === 404) {
            recoverUnusableSession(sqState.sessionId!)
            return
          }
          // Session no longer exists or network error — fall back to router-state
          // (or zeros if coming from sessionStorage with no counters).
          doResume(
            sqState.resolutionCards ?? sqState.reviewedCards ?? 0,
            sqState.reviewedCards ?? 0,
            sqState.totalCards ?? 0,
            sqState.reviewedCorrect ?? 0,
            sqState.reviewedWrong ?? 0,
          )
        })

      // Seed the completion screen's mistakes list from the server (read-only
      // reconstruction of ReviewLog — see learning_service.get_session_mistakes
      // and docs/diagnostics/retype-step-lost-on-reload-2026-08-26.md §3.6/§7).
      // The client accumulates mistakes in memory as the learner answers, so a
      // fresh page load (this whole branch) always starts with none; without
      // this call the list would silently miss every wrong answer from before
      // the pause/reload while the completion screen's wrong count — read from
      // the server at /complete — kept counting them. Independent of the
      // counter fetch above: its own failure leaves the list to accumulate
      // from this point forward (no worse than before this call existed), and
      // must not block or fall back into `doResume`, whose own catch branch
      // has no mistakes data to offer either.
      api
        .get<MistakeItemDTO[]>(`/api/v1/learning/sessions/${sqState.sessionId}/mistakes`)
        .then((r) => setMistakes(r.data.map(mapMistakeDTO)))
        .catch(() => { /* leave mistakes to accumulate from here on */ })
      return
    }

    if (!sourceId) {
      setErrorMsg('Missing source.')
      setPhase('error')
      return
    }
    // StrictMode double-invokes this effect in dev (mount → cleanup → remount,
    // same instance — refs survive it, state doesn't reset). Without this guard
    // a second POST /sessions fires for the same page load; its response finds
    // the first response's fresh session (already today's, no longer boundary-
    // closed) and can race to silently clear dayBoundaryClosed right after the
    // first response set it. A real remount (leaving and returning to Learn) is
    // a new component instance with a fresh ref, so this never blocks that.
    beginSourceSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Watch next-card query to drive the phase machine ────────────────────────
  useEffect(() => {
    if (!awaitingCard.current) return
    if (nextCardQuery.isFetching) return

    if (nextCardQuery.isError) {
      awaitingCard.current = false
      if (isDayEndedError(nextCardQuery.error)) {
        // ADR-082: the session began before today's boundary and was just
        // closed. currentCard is deliberately left as-is — it stays visible,
        // frozen, behind the overlay, which is the only thing this sets.
        setDayBoundaryClosed('interrupted')
        return
      }
      if (httpStatusOf(nextCardQuery.error) === 409 && sessionId) {
        // ADR-103 Decision 7: the session is no longer usable — replace it.
        recoverUnusableSession(sessionId)
        return
      }
      // Localized default text (tr.learn.sessionError) — no hard-coded message.
      setErrorMsg('')
      setPhase('error')
      return
    }
    if (!nextCardQuery.isSuccess) return

    awaitingCard.current = false

    const rawData: NextCardData | null = nextCardQuery.data ?? null
    const sessionDone =
      rawData === null ||
      (rawData !== null && rawData.session_complete === true)

    let focusTimer: ReturnType<typeof setTimeout> | undefined

    if (sessionDone) {
      sessionEndedRef.current = true
      if (rawData !== null && rawData.session_complete === true) {
        setStats({ total: rawData.total, correct: rawData.correct, wrong: rawData.wrong })
        // new_words_today: cross-session daily count from backend (falls back to per-session
        // new_words for older server versions that don't yet return the field).
        setWordsLearnedToday(rawData.new_words_today ?? rawData.new_words)
      }
      void qc.invalidateQueries({ queryKey: ['progress-summary'] })
      // Mastery levels changed during this session — refresh all deck/card caches so
      // MasteryMeter reflects the new levels without requiring a manual remount/refresh.
      // Broad prefix invalidation covers all decks (Smart Queue can touch multiple decks).
      void qc.invalidateQueries({ queryKey: ['cards'] })
      void qc.invalidateQueries({ queryKey: ['deck'] })
      void qc.invalidateQueries({ queryKey: ['decks'] })
      void qc.invalidateQueries({ queryKey: ['active-session'] })
      void qc.invalidateQueries({ queryKey: ['active-session-sq'] })
      void qc.invalidateQueries({ queryKey: ['learning-session-status', sessionId] })
      void resolveWordsKnownAndComplete()
    } else {
      const nextCard = rawData as LearningNextCard
      // A card was served: whatever recovery led here worked.
      consecutiveRecoveriesRef.current = 0
      // Snapshot the just-answered card as prevCard, but carry the post-answer
      // mastery_level from lastResult — not the pre-answer level baked into the
      // currentCard object from the /next-card response. lastResult is readable
      // here because setLastResult(null) below is a state write that only takes
      // effect in the next render; the live card indicator uses the same value
      // via displayMastery (ADR-014 amendment 2026-07-06).
      setPrevCard((_prev) => {
        if (!currentCard) return _prev
        const postAnswerLevel = lastResult?.mastery_level
        return postAnswerLevel !== undefined
          ? { ...currentCard, mastery_level: postAnswerLevel }
          : currentCard
      })
      setShowingPrev(false)
      speechFiredWrongRef.current = false
      speechFiredCorrectRef.current = false
      setCurrentCard(nextCard)
      if (nextCard.is_practice) setIsPractice(true)
      setUserInput('')
      setLastResult(null)
      setActiveTab(0)
      setResultState('none')
      setMcSelectedOption(null)
      setHintLetters(0)
      setUsedHint(false)
      setShowDefinitionInline(false)
      setPhase('active')
    }

    return () => {
      if (focusTimer) clearTimeout(focusTimer)
    }
    // Note: input refocus on card change is handled synchronously by LearnPage's
    // useLayoutEffect keyed on currentCard?.card_id. Using setTimeout here opens
    // the Android IME-retract window and dismisses the keyboard (ADR-014).
  }, [nextCardQuery.isFetching, nextCardQuery.isSuccess, nextCardQuery.isError, nextCardQuery.data])

  // Retype-state focus is handled synchronously by LearnPage's useLayoutEffect
  // keyed on resultState — no setTimeout here (Android IME retract window).

  // ── Reset speech-fired guards when the card changes ─────────────────────────
  useEffect(() => {
    speechFiredWrongRef.current = false
    speechFiredCorrectRef.current = false
    retypeSucceededRef.current = false
    currentCardGrammarNoteRef.current = currentCard?.grammar_note ?? null
  }, [currentCard?.card_id, currentCard?.grammar_note])

  // ── Finalize session server-side and transition to the completion screen ───────
  // Called by both Path C (doAdvance resolution cap) and the grammar-note cap path.
  // Calls POST /complete to mark the session done and retrieve the authoritative stats
  // snapshot — this fixes the completion screen and ensures Home "Continue" disappears.
  const finalizeAndComplete = useCallback(async (sid: string | undefined) => {
    sessionEndedRef.current = true
    void qc.invalidateQueries({ queryKey: ['progress-summary'] })
    void qc.invalidateQueries({ queryKey: ['cards'] })
    void qc.invalidateQueries({ queryKey: ['deck'] })
    void qc.invalidateQueries({ queryKey: ['decks'] })
    void qc.invalidateQueries({ queryKey: ['active-session'] })
    void qc.invalidateQueries({ queryKey: ['active-session-sq'] })
    void qc.invalidateQueries({ queryKey: ['learning-session-status', sid] })

    // ADR-086 Decision 5: completion never outruns the counter. The terminal
    // typed retype (diagnostic L1) is exactly the case where one interaction
    // produces both the last resolution and the condition that ends the session,
    // and the resolution goes first.
    //
    // Briefly bounded, and the bound costs nothing: a report still outstanding
    // when the wait expires is neither discarded nor written off — it keeps its
    // session and keeps being delivered, so a late acknowledgement still books.
    // Waiting longer would only delay this screen on a network that is already
    // failing.
    await reporterRef.current?.waitBeforeCompletion()

    if (sid) {
      try {
        const { data } = await api.post<{
          reviewed: number; correct: number; wrong: number
          new_words: number; new_words_today: number
        }>(`/api/v1/learning/sessions/${sid}/complete`)
        setStats({ total: data.reviewed, correct: data.correct, wrong: data.wrong })
        setWordsLearnedToday(data.new_words_today ?? data.new_words)
      } catch {
        // Network error: client-accumulated stats remain on-screen rather than showing zeros.
      }
    }
    await resolveWordsKnownAndComplete()
  }, [qc, resolveWordsKnownAndComplete])

  // ── Advance to next card (hoisted so it can be stored in a ref for manual advance) ──
  const doAdvance = useCallback(() => {
    pendingAdvanceRef.current = null
    const note = currentCardGrammarNoteRef.current
    if (note && !grammarNoteShownRef.current) {
      grammarNoteShownRef.current = true
      setPendingGrammarNote(note)
      return
    }
    // Stop immediately once every resolution slot is filled — never request card N+1.
    // Call /complete to finalize server-side (fixes "Continue" and completion counters).
    if (totalCards > 0 && resolutionCountRef.current >= totalCards) {
      void finalizeAndComplete(sessionId)
      return
    }
    awaitingCard.current = true
    qc.invalidateQueries({ queryKey: ['learning-next-card', sessionId] })
  }, [qc, sessionId, totalCards, finalizeAndComplete])

  // ── Auto-play audio + auto-advance on verdict ───────────────────────────────
  useEffect(() => {
    // `cancelled` is flipped by the cleanup so the speech onEnd callback — which
    // outlives the effect — can't fire doAdvance after the effect re-runs or unmounts.
    let cancelled = false

    if (resultState === 'correct' && !speechFiredCorrectRef.current) {
      speechFiredCorrectRef.current = true
      // Store so handleManualAdvance can fire it directly (e.g. Enter key).
      pendingAdvanceRef.current = doAdvance

      if (retypeSucceededRef.current) {
        // Retype just completed — speak the example sentence (term substituted) then advance,
        // same as a first-try correct answer. The wrong-phase already spoke the bare term;
        // now we reinforce with the full context sentence.
        retypeSucceededRef.current = false
        if (isSpeechSupported && promptToSpeak) {
          let called = false
          const onDone = () => {
            if (cancelled || called) return
            called = true
            if (advanceFallbackTimer.current !== null) {
              clearTimeout(advanceFallbackTimer.current)
              advanceFallbackTimer.current = null
            }
            doAdvance()
          }
          speak(promptToSpeak, onDone)
          advanceFallbackTimer.current = setTimeout(onDone, _ttsCap(promptToSpeak))
        } else {
          advanceFallbackTimer.current = setTimeout(() => {
            if (cancelled) return
            doAdvance()
          }, 600)
        }
      } else if (isSpeechSupported && promptToSpeak) {
        // Correct on first try: speak the example (or term) then advance.
        // Cap is proportional to text length so long sentences finish before the
        // session advances; a hard ceiling prevents a stuck TTS from freezing forever.
        let called = false
        const onDone = () => {
          if (cancelled || called) return
          called = true
          if (advanceFallbackTimer.current !== null) {
            clearTimeout(advanceFallbackTimer.current)
            advanceFallbackTimer.current = null
          }
          doAdvance()
        }
        speak(promptToSpeak, onDone)
        advanceFallbackTimer.current = setTimeout(onDone, _ttsCap(promptToSpeak))
      } else {
        // No TTS — short fixed delay so the ✓ feedback registers visually
        advanceFallbackTimer.current = setTimeout(() => {
          if (cancelled) return
          doAdvance()
        }, 1200)
      }
    } else if (resultState === 'wrong' && !speechFiredWrongRef.current) {
      speechFiredWrongRef.current = true
      const canRetry = wrongCanRetryRef.current

      // Guard so the fallback timer and the speech callback don't both fire.
      let afterSpeakCalled = false
      const afterSpeak = () => {
        if (cancelled || afterSpeakCalled) return
        afterSpeakCalled = true
        // Clear the safety timer in case it was speech that fired first.
        if (advanceFallbackTimer.current !== null) {
          clearTimeout(advanceFallbackTimer.current)
          advanceFallbackTimer.current = null
        }
        if (wrongExerciseModeRef.current === 'text') {
          // Standard typed-input wrong answer: always enter retype mode.
          // The user must type the correct term before the session advances.
          // LearnPage's useLayoutEffect on resultState handles refocus
          // synchronously; no setTimeout here (Android IME retract — ADR-014).
          // Do NOT clear userInput here: handleSubmit already cleared it when
          // entering 'wrong', and the user may have started typing during audio
          // playback (input is editable in 'wrong' state). Clearing again would
          // discard keystrokes captured before this callback fired.
          setResultState('retype')
        } else if (canRetry) {
          // MC / sentence-build / listening re-queued: old retry behaviour.
          speechFiredWrongRef.current = false
          setLastResult(null)
          setUserInput('')
          setResultState('none')
        } else {
          // MC / sentence-build / listening not re-queued: advance via doAdvance
          // so the session-end cap and grammar note check are both respected.
          doAdvance()
        }
      }

      // Wrong: speak the correct term aloud, then either retry or advance.
      // Safety fallback fires if the speech synthesis callback is never called
      // (e.g. browser cancels synthesis silently) — caps the blocked state at 4s.
      if (isSpeechSupported && termToSpeak) {
        speak(termToSpeak, afterSpeak)
        advanceFallbackTimer.current = setTimeout(afterSpeak, 4000)
      } else {
        advanceFallbackTimer.current = setTimeout(afterSpeak, 1200)
      }
    }

    return () => {
      cancelled = true
      if (advanceFallbackTimer.current !== null) {
        clearTimeout(advanceFallbackTimer.current)
        advanceFallbackTimer.current = null
      }
    }
  }, [resultState, isSpeechSupported, promptToSpeak, termToSpeak, speak, qc, sessionId, doAdvance])

  // ── Drop the stored advance when the session changes so handleManualAdvance
  //    can't invoke a doAdvance bound to a stale sessionId. ────────────────────
  useEffect(() => {
    pendingAdvanceRef.current = null
  }, [sessionId])

  // ── Manual advance — called when the user presses Enter during 'correct' state ──
  // Cancels in-flight TTS (without triggering onEnd) and fires the pending advance
  // immediately so the session never appears frozen while a long example plays.
  const handleManualAdvance = useCallback(() => {
    const advance = pendingAdvanceRef.current
    if (!advance) return  // already advanced, or not in correct state
    cancelSpeech()
    if (advanceFallbackTimer.current !== null) {
      clearTimeout(advanceFallbackTimer.current)
      advanceFallbackTimer.current = null
    }
    advance()
  }, [cancelSpeech])

  // ── Pause session when navigating away mid-session ──────────────────────────
  useEffect(() => {
    return () => {
      if (sessionId && !sessionEndedRef.current) {
        pauseSession.mutate(sessionId)
        // Invalidate Home's session queries so Continue reflects the paused state
        // immediately on next visit rather than waiting out the 30s staleTime.
        void qc.invalidateQueries({ queryKey: ['active-session'] })
        void qc.invalidateQueries({ queryKey: ['active-session-sq'] })
        void qc.invalidateQueries({ queryKey: ['learning-session-status', sessionId] })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  // ── Submit answer ───────────────────────────────────────────────────────────
  const handleSubmit = useCallback(() => {
    // Allow resubmit from soft_reject — the user is retrying freely (ADR-048 §1).
    if (!currentCard || (resultState !== 'none' && resultState !== 'soft_reject') || submitAnswer.isPending) return
    // ADR-086 Decision 3b: offer any outstanding report again alongside this
    // session-scoped call. Not awaited — the answer never waits on it.
    reofferReports()
    submitAnswer.mutate(
      { card_id: currentCard.card_id, user_answer: userInput.trim(), hint_used: usedHint },
      {
        onSuccess: (result) => {
          setLastResult(result)
          // Soft reject: zero state writes, no stats update, no firstAttemptedRef add.
          // The eventual correct after a soft reject counts as first-attempt correct (ADR-048 §2).
          if ('is_soft_reject' in result && result.is_soft_reject) {
            // Presentation amendment (ADR-048 §7, 2026-07-15): the typed synonym
            // is cleared automatically so the guidance can live in the input's
            // placeholder rather than a reserved feedback zone.
            setUserInput('')
            setResultState('soft_reject')
            return
          }
          const isFirstAttempt = !firstAttemptedRef.current.has(currentCard.card_id)
          if (isFirstAttempt) firstAttemptedRef.current.add(currentCard.card_id)
          setStats((s) => ({
            total: isFirstAttempt ? s.total + 1 : s.total,
            correct: s.correct + (isFirstAttempt && result.is_correct ? 1 : 0),
            wrong: s.wrong + (isFirstAttempt && !result.is_correct ? 1 : 0),
          }))
          if (isFirstAttempt && currentCard.block_type) {
            const bt = currentCard.block_type
            setBlockStats((prev) => ({
              ...prev,
              [bt]: {
                correct: (prev[bt]?.correct ?? 0) + (result.is_correct ? 1 : 0),
                wrong: (prev[bt]?.wrong ?? 0) + (!result.is_correct ? 1 : 0),
              },
            }))
          }
          if (result.is_correct) {
            resolutionCountRef.current += 1
            setResolutionCount(resolutionCountRef.current)
            addTodayCorrect(1)  // localStorage counter for other consumers; not used as display source
            setResultState('correct')
          } else {
            if (currentCard) {
              // Capture the raw typed input now — the mandatory retype step
              // below overwrites userInput before the user can see this screen again.
              recordMistake(currentCard, result.correct_answer, userInput.trim())
            }
            setUserInput('')
            wrongCanRetryRef.current = result.require_retype
            wrongExerciseModeRef.current = 'text'
            setResultState('wrong')
          }
        },
        onError: (err) => {
          if (isDayEndedError(err)) { setDayBoundaryClosed('interrupted'); return }
          setErrorMsg(extractErrorMessage(err, 'Failed to submit answer. Please try again.'))
          setPhase('error')
        },
      }
    )
  }, [currentCard, userInput, resultState, submitAnswer, usedHint, reofferReports, recordMistake])

  // ── Multiple-choice selection ───────────────────────────────────────────────
  const handleMCSelect = useCallback((option: string) => {
    if (!currentCard || resultState !== 'none' || submitAnswer.isPending) return
    setMcSelectedOption(option)
    // ADR-086 Decision 3b: offer any outstanding report again alongside this
    // session-scoped call. Not awaited — the answer never waits on it.
    reofferReports()
    submitAnswer.mutate(
      { card_id: currentCard.card_id, user_answer: option, is_multiple_choice: true },
      {
        onSuccess: (result) => {
          setLastResult(result)
          const isFirstAttempt = !firstAttemptedRef.current.has(currentCard.card_id)
          if (isFirstAttempt) firstAttemptedRef.current.add(currentCard.card_id)
          setStats((s) => ({
            total: isFirstAttempt ? s.total + 1 : s.total,
            correct: s.correct + (isFirstAttempt && result.is_correct ? 1 : 0),
            wrong: s.wrong + (isFirstAttempt && !result.is_correct ? 1 : 0),
          }))
          if (isFirstAttempt && currentCard.block_type) {
            const bt = currentCard.block_type
            setBlockStats((prev) => ({
              ...prev,
              [bt]: {
                correct: (prev[bt]?.correct ?? 0) + (result.is_correct ? 1 : 0),
                wrong: (prev[bt]?.wrong ?? 0) + (!result.is_correct ? 1 : 0),
              },
            }))
          }
          if (result.is_correct) {
            resolutionCountRef.current += 1
            setResolutionCount(resolutionCountRef.current)
            addTodayCorrect(1)  // localStorage counter for other consumers; not used as display source
            setResultState('correct')
          } else {
            if (currentCard) {
              // NOTE (ADR-048): `result` here is `AnswerWrongResult | AnswerSoftRejectResult`.
              // MC answers CAN soft-reject server-side (backend does not exclude MC), and this
              // handler has no soft-reject branch — a soft-reject is currently treated as wrong.
              // `?? ''` preserves that existing behavior; correct handling is deferred (on-device).
              recordMistake(currentCard, result.correct_answer ?? '', option)
            }
            // Wrong MC: card advances past the screen (doAdvance called from wrong-state
            // effect when canRetry=false). Count the presentation leaving the conveyor.
            resolutionCountRef.current += 1
            setResolutionCount(resolutionCountRef.current)
            wrongCanRetryRef.current = false  // MC wrong never retries inline
            wrongExerciseModeRef.current = 'other'
            setResultState('wrong')
          }
        },
        onError: (err) => {
          setMcSelectedOption(null)
          if (isDayEndedError(err)) { setDayBoundaryClosed('interrupted'); return }
          setErrorMsg(extractErrorMessage(err, 'Failed to submit answer. Please try again.'))
          setPhase('error')
        },
      },
    )
  }, [currentCard, resultState, submitAnswer, reofferReports, recordMistake])

  // ── Retype submission ───────────────────────────────────────────────────────
  const handleRetype = useCallback(() => {
    if (!currentCard || !lastResult || lastResult.is_correct || resultState !== 'retype') return
    const correct = lastResult.correct_answer ?? ''
    // ADR-086 Decision 2's "same acceptance rule" as the backend's
    // check_retype_answer (ADR-049 diacritic fold) — see answerNormalize.ts.
    if (checkRetypeAnswer(userInput, correct)) {
      retypeSucceededRef.current = true
      // Retype accepted: the conveyor advances — card leaves the screen.
      // Count it now; the card re-queued but the slot is consumed.
      resolutionCountRef.current += 1
      setResolutionCount(resolutionCountRef.current)
      // ADR-086 Decision 1: report it with the event that created it. The
      // acceptance above already happened — this call is fire-and-forget and
      // nothing the learner sees waits on it, which is Decision 2's requirement
      // that no round trip sit between the keystroke and the acceptance.
      // Validation stays entirely client-side: the retyped string is never sent.
      //
      // One accepted retype yields one identity. The Set guards a double-invoked
      // handler from minting two ids for one resolution; the server's unique
      // constraint guards two deliveries of one id (ADR-086 Decision 4).
      const retypeKey = `${currentCard.card_id}:${resolutionCountRef.current}`
      const sid = sessionIdRef.current
      if (sid && !reportedRetypesRef.current.has(retypeKey)) {
        reportedRetypesRef.current.add(retypeKey)
        reporterRef.current?.report({
          resolutionId: crypto.randomUUID(),
          kind: 'typed_retype',
          cardId: currentCard.card_id,
          sessionId: sid,
        })
      }
      setResultState('correct')
    } else {
      setIsShaking(true)
      setUserInput('')
      setTimeout(() => {
        setIsShaking(false)
        inputRef.current?.focus()
      }, 420)
    }
  }, [currentCard, lastResult, resultState, userInput])

  // ── Listening exercise handlers ─────────────────────────────────────────────
  const handleListeningSubmit = useCallback((answer: string) => {
    if (!currentCard || resultState !== 'none' || submitAnswer.isPending) return
    // ADR-086 Decision 3b: offer any outstanding report again alongside this
    // session-scoped call. Not awaited — the answer never waits on it.
    reofferReports()
    submitAnswer.mutate(
      { card_id: currentCard.card_id, user_answer: answer.trim(), hint_used: false },
      {
        onSuccess: (result) => {
          setLastResult(result)
          const isFirstAttempt = !firstAttemptedRef.current.has(currentCard.card_id)
          if (isFirstAttempt) firstAttemptedRef.current.add(currentCard.card_id)
          setStats((s) => ({
            total: isFirstAttempt ? s.total + 1 : s.total,
            correct: s.correct + (isFirstAttempt && result.is_correct ? 1 : 0),
            wrong: s.wrong + (isFirstAttempt && !result.is_correct ? 1 : 0),
          }))
          if (result.is_correct) {
            resolutionCountRef.current += 1
            setResolutionCount(resolutionCountRef.current)
            addTodayCorrect(1)  // localStorage counter for other consumers; not used as display source
            setResultState('correct')
          } else {
            if (currentCard) {
              // NOTE (ADR-048): listening answers CAN soft-reject server-side (typed input,
              // not sentence_build), and this handler has no soft-reject branch — a soft-reject
              // is currently treated as wrong. `?? ''` / `?? false` preserve that existing
              // behavior (undefined require_retype was already falsy); correct handling is
              // deferred (on-device).
              recordMistake(currentCard, result.correct_answer ?? '', answer.trim())
            }
            wrongCanRetryRef.current = result.require_retype ?? false
            wrongExerciseModeRef.current = 'other'
            setResultState('wrong')
          }
        },
        onError: (err) => {
          if (isDayEndedError(err)) { setDayBoundaryClosed('interrupted'); return }
          setErrorMsg(extractErrorMessage(err, 'Failed to submit answer. Please try again.'))
          setPhase('error')
        },
      },
    )
  }, [currentCard, resultState, submitAnswer, reofferReports, recordMistake])

  const handleListeningRetype = useCallback((answer: string) => {
    if (!currentCard || !lastResult || lastResult.is_correct || resultState !== 'retype' || retypeMutation.isPending) return
    retypeMutation.mutate(
      { card_id: currentCard.card_id, user_answer: answer.trim() },
      {
        onSuccess: (result) => {
          if (result.accepted) {
            // Listening retype accepted: conveyor advances.
            resolutionCountRef.current += 1
            setResolutionCount(resolutionCountRef.current)
            setResultState('correct')
          } else {
            setIsShaking(true)
            setTimeout(() => setIsShaking(false), 420)
          }
        },
        // No other failure mode was previously reported here (the network
        // contract otherwise silently drops a retype error) — this handler
        // is scoped exactly to the ADR-082 signal and adds nothing else.
        onError: (err) => {
          if (isDayEndedError(err)) setDayBoundaryClosed('interrupted')
        },
      },
    )
  }, [currentCard, lastResult, resultState, retypeMutation])

  const handleListeningContinue = useCallback(() => {
    window.speechSynthesis?.cancel()
    awaitingCard.current = true
    qc.invalidateQueries({ queryKey: ['learning-next-card', sessionId] })
  }, [qc, sessionId])

  // ── Sentence-build submission ───────────────────────────────────────────────
  const handleSBSubmit = useCallback((answer: string) => {
    if (!currentCard || resultState !== 'none' || submitAnswer.isPending) return
    // ADR-086 Decision 3b: offer any outstanding report again alongside this
    // session-scoped call. Not awaited — the answer never waits on it.
    reofferReports()
    submitAnswer.mutate(
      { card_id: currentCard.card_id, user_answer: answer, is_sentence_build: true },
      {
        onSuccess: (result) => {
          setLastResult(result)
          const isFirstAttempt = !firstAttemptedRef.current.has(currentCard.card_id)
          if (isFirstAttempt) firstAttemptedRef.current.add(currentCard.card_id)
          setStats((s) => ({
            total: isFirstAttempt ? s.total + 1 : s.total,
            correct: s.correct + (isFirstAttempt && result.is_correct ? 1 : 0),
            wrong: s.wrong + (isFirstAttempt && !result.is_correct ? 1 : 0),
          }))
          if (result.is_correct) {
            resolutionCountRef.current += 1
            setResolutionCount(resolutionCountRef.current)
            addTodayCorrect(1)  // localStorage counter for other consumers; not used as display source
            setResultState('correct')
          } else {
            // sentence_build: backend excludes this format from soft-reject (learning.py),
            // so `result` is always AnswerWrongResult at runtime here; `?? ''` is type-only.
            recordMistake(currentCard, result.correct_answer ?? '', answer)
            wrongExerciseModeRef.current = 'other'
            setResultState('wrong')
          }
        },
        onError: (err) => {
          if (isDayEndedError(err)) { setDayBoundaryClosed('interrupted'); return }
          setErrorMsg(extractErrorMessage(err, 'Failed to submit answer. Please try again.'))
          setPhase('error')
        },
      },
    )
  }, [currentCard, resultState, submitAnswer, reofferReports, recordMistake])

  // ── Grammar note continue (called from render, needs qc + sessionId) ─────────
  const handleGrammarNoteContinue = useCallback(() => {
    setPendingGrammarNote(null)
    if (totalCards > 0 && resolutionCountRef.current >= totalCards) {
      void finalizeAndComplete(sessionId)
      return
    }
    awaitingCard.current = true
    qc.invalidateQueries({ queryKey: ['learning-next-card', sessionId] })
  }, [qc, sessionId, totalCards, finalizeAndComplete])

  // ── Complete screen: continue / review mistakes ─────────────────────────────
  const handleContinueLearning: (() => void) | undefined = (() => {
    if (sqState?.sessionId) {
      // Smart-queue session: start a fresh SQ session in-place (same queue rules).
      return () => {
        startSmartQueue.mutate(undefined, {
          onSuccess: (data) => {
            if (!data.session_id || data.total_cards === 0) {
              setContinueExhausted(true)
              return
            }
            resetForNewSession()
            setTotalCards(data.total_cards)
            if (data.is_resumed) {
              resolutionCountRef.current = data.resolution_count
              setResolutionCount(data.resolution_count)
            }
            awaitingCard.current = true
            setSessionId(data.session_id)
          },
          onError: () => navigate('/home'),
        })
      }
    }
    if (sourceId) {
      // Source-bound session (deck / folder / course): restart the same source.
      return () => {
        resetForNewSession()
        createSession.mutate(
          { source_type: sourceType, source_id: sourceId },
          {
            onSuccess: (data) => {
              if (data.total_cards === 0) {
                sessionEndedRef.current = true
                setPhase('empty')
                return
              }
              setTotalCards(data.total_cards)
              awaitingCard.current = true
              setSessionId(data.session_id)
            },
            onError: (err: unknown) => {
              const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
              const code = detail && typeof detail === 'object' ? (detail as Record<string, unknown>).code : undefined
              if (code === 'NO_DUE_CARDS') {
                const raw = detail && typeof detail === 'object' ? (detail as Record<string, unknown>).next_due_at : undefined
                setNextDueAt(typeof raw === 'string' ? raw : null)
                sessionEndedRef.current = true
                setPhase('empty')
                return
              }
              navigate('/home')
            },
          },
        )
      }
    }
    return undefined
  })()

  return {
    // Phase
    phase,
    errorMsg,
    nextDueAt,
    // Session info
    sessionId,
    isResumed,
    lessonPlan,
    prepStats,
    isPractice,
    totalCards,
    resolutionCount,
    stats,
    wordsLearnedToday,
    mistakes,
    blockStats,
    // Card state
    currentCard,
    prevCard,
    showingPrev,
    setShowingPrev,
    userInput,
    setUserInput,
    lastResult,
    resultState,
    setResultState,
    activeTab,
    setActiveTab,
    showAll,
    setShowAll,
    showDefinitionInline,
    setShowDefinitionInline,
    isShaking,
    hintLetters,
    setHintLetters,
    usedHint,
    setUsedHint,
    pendingGrammarNote,
    setPendingGrammarNote,
    mcSelectedOption,
    // ADR-082 boundary overlay
    dayBoundaryClosed,
    restartingSession,
    continueAfterDayBoundary,
    // ADR-103 Decision 7 — the error screen's Retry
    retrySession,
    // Derived
    termToSpeak,
    promptToSpeak,
    mcOptions,
    // Refs
    inputRef,
    awaitingCard,
    // Handlers
    handleSubmit,
    handleMCSelect,
    handleRetype,
    handleListeningSubmit,
    handleListeningRetype,
    handleListeningContinue,
    handleSBSubmit,
    handleGrammarNoteContinue,
    handleManualAdvance,
    continueExhausted,
    handleContinueLearning,
    // Mutation states
    submitIsPending: submitAnswer.isPending,
    retypeIsPending: retypeMutation.isPending,
    // Progress (for completion screen)
    progressSummary,
    milestone,
    // Deterministically-resolved post-session vocabulary total — same value used for
    // milestone detection, so the completion screen's support line and the detection
    // input are the same number by construction. Null until resolved (falls back to
    // the live progressSummary total for display purposes only).
    postSessionWordsKnown,
  }
}
